/**
 * Enquiry Form Component
 *
 * @package
 */

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { isValidPhone } from '../helpers/validatePhone';

const EnquiryForm = ( {
	selectedDealer,
	vehicleConfigId,
	selectedBaseModel,
	selectedProducts,
	shareableCode,
	vatMode,
	quantities = {},
	onSuccess,
} ) => {
	const [ formData, setFormData ] = useState( {
		name: '',
		email: '',
		phone: '',
		message: '',
	} );
	const [ hasConsented, setHasConsented ] = useState( false );
	const [ errors, setErrors ] = useState( {} );
	const [ isSubmitting, setIsSubmitting ] = useState( false );

	const validateForm = () => {
		const newErrors = {};

		if ( ! formData.name.trim() ) {
			newErrors.name = __( 'Name is required', 'at-vehicle-configurator' );
		}

		if ( ! formData.email.trim() ) {
			newErrors.email = __( 'Email is required', 'at-vehicle-configurator' );
		} else if ( ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( formData.email ) ) {
			newErrors.email = __( 'Invalid email format', 'at-vehicle-configurator' );
		}

		if ( ! formData.phone.trim() ) {
			newErrors.phone = __( 'Phone is required', 'at-vehicle-configurator' );
		} else if ( ! isValidPhone( formData.phone ) ) {
			newErrors.phone = __( 'Invalid phone number format', 'at-vehicle-configurator' );
		}

		if ( ! hasConsented ) {
			newErrors.consent = __( 'Please agree to us processing your details before sending.', 'at-vehicle-configurator' );
		}

		setErrors( newErrors );
		return Object.keys( newErrors ).length === 0;
	};

	const handleSubmit = async e => {
		e.preventDefault();

		if ( ! validateForm() ) {
			return;
		}

		if ( ! selectedDealer && ! vehicleConfigId ) {
			setErrors( { general: __( 'Please select a dealership first.', 'at-vehicle-configurator' ) } );
			return;
		}

		setIsSubmitting( true );

		const requestData = {
			...formData,
			base_model: selectedBaseModel > 0 ? selectedBaseModel : null,
			products: selectedProducts || [],
			// Both are sent: the server routes on dealer_id first and needs
			// vehicle_config_id for the attached PDF's cover page either way.
			dealer_id: selectedDealer || null,
			vehicle_config_id: vehicleConfigId || null,
			shareable_code: shareableCode || null,
			page_url: window.location.origin + window.location.pathname,
			vat_mode: vatMode || 'including',
			quantities,
			consent: true,
		};

		try {
			await apiFetch( {
				path: '/at-configurator/v1/enquiry',
				method: 'POST',
				data: requestData,
			} );

			if ( onSuccess ) {
				onSuccess();
			}

			setFormData( {
				name: '',
				email: '',
				phone: '',
				message: '',
			} );
			setHasConsented( false );
		} catch ( error ) {
			setErrors( { general: __( 'Error sending enquiry. Please try again.', 'at-vehicle-configurator' ) } );
		} finally {
			setIsSubmitting( false );
		}
	};

	const nameId = 'enquiry-name';
	const emailId = 'enquiry-email';
	const phoneId = 'enquiry-phone';
	const messageId = 'enquiry-message';
	const consentId = 'enquiry-consent';
	const nameErrorId = `${ nameId }-error`;
	const emailErrorId = `${ emailId }-error`;
	const phoneErrorId = `${ phoneId }-error`;
	const consentErrorId = `${ consentId }-error`;

	return (
		<form onSubmit={ handleSubmit } className="configurator-enquiry-form">
			{ /* ARIA live region for form errors */ }
			<div role="status" aria-live="polite" aria-atomic="true" className="screen-reader-text">
				{ Object.keys( errors ).length > 0 && (
					<div>
						{ __( 'Form has errors. Please check the fields below.', 'at-vehicle-configurator' ) }
					</div>
				) }
			</div>
			{ errors.general && (
				<div className="configurator-form-error" role="alert">
					{ errors.general }
				</div>
			) }
			<div className="configurator-form-field">
				<label htmlFor={ nameId }>
					{ __( 'Name', 'at-vehicle-configurator' ) } *
					<input
						id={ nameId }
						type="text"
						value={ formData.name }
						onChange={ e =>
							setFormData( { ...formData, name: e.target.value } )
						}
						required
						aria-required="true"
						aria-invalid={ !! errors.name }
						aria-describedby={ errors.name ? nameErrorId : undefined }
					/>
					{ errors.name && (
						<span id={ nameErrorId } className="configurator-form-error" role="alert">
							{ errors.name }
						</span>
					) }
				</label>
			</div>
			<div className="configurator-form-field">
				<label htmlFor={ emailId }>
					{ __( 'Email', 'at-vehicle-configurator' ) } *
					<input
						id={ emailId }
						type="email"
						value={ formData.email }
						onChange={ e =>
							setFormData( { ...formData, email: e.target.value } )
						}
						required
						aria-required="true"
						aria-invalid={ !! errors.email }
						aria-describedby={ errors.email ? emailErrorId : undefined }
					/>
					{ errors.email && (
						<span id={ emailErrorId } className="configurator-form-error" role="alert">
							{ errors.email }
						</span>
					) }
				</label>
			</div>
			<div className="configurator-form-field">
				<label htmlFor={ phoneId }>
					{ __( 'Phone', 'at-vehicle-configurator' ) } *
					<input
						id={ phoneId }
						type="tel"
						value={ formData.phone }
						onChange={ e =>
							setFormData( { ...formData, phone: e.target.value } )
						}
						required
						aria-required="true"
						aria-invalid={ !! errors.phone }
						aria-describedby={ errors.phone ? phoneErrorId : undefined }
					/>
					{ errors.phone && (
						<span id={ phoneErrorId } className="configurator-form-error" role="alert">
							{ errors.phone }
						</span>
					) }
				</label>
			</div>
			<div className="configurator-form-field">
				<label htmlFor={ messageId }>
					{ __( 'Message', 'at-vehicle-configurator' ) }
					<textarea
						id={ messageId }
						value={ formData.message }
						onChange={ e =>
							setFormData( { ...formData, message: e.target.value } )
						}
						rows="4"
					/>
				</label>
			</div>
			<p className="configurator-form-privacy-note">
				{ __( '*By sending your enquiry, Arctic Trucks Sverige processes your personal data in order to handle your enquiry and contact you.', 'at-vehicle-configurator' ) }
				{ ' ' }
				<a href={ atConfigurator.privacyPolicyUrl } target="_blank" rel="noopener noreferrer">
					{ __( 'Read more in our privacy policy.', 'at-vehicle-configurator' ) }
				</a>
			</p>
			<div className="configurator-form-consent">
				<label htmlFor={ consentId }>
					<input
						id={ consentId }
						type="checkbox"
						checked={ hasConsented }
						onChange={ e => setHasConsented( e.target.checked ) }
						required
						aria-required="true"
						aria-invalid={ !! errors.consent }
						aria-describedby={ errors.consent ? consentErrorId : undefined }
					/>
					<span>
						{ __( 'I consent to Arctic Trucks processing my personal data in order to answer this enquiry.', 'at-vehicle-configurator' ) }
					</span>
				</label>
				{ errors.consent && (
					<span id={ consentErrorId } className="configurator-form-error" role="alert">
						{ errors.consent }
					</span>
				) }
			</div>
			<button
				type="submit"
				disabled={ isSubmitting || ( ! selectedDealer && ! vehicleConfigId ) }
				className="configurator-button configurator-button-primary"
			>
				{ isSubmitting
					? __( 'Sending…', 'at-vehicle-configurator' )
					: __( 'Send Enquiry', 'at-vehicle-configurator' ) }
			</button>
		</form>
	);
};

export default EnquiryForm;
